import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaBuilding, FaMapMarkerAlt, FaEnvelope, FaPhone, FaCalendarAlt, FaChartLine } from 'react-icons/fa';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import './CreateCompany.css';

const CreateCompany = () => {
    const navigate = useNavigate();
    const baseUrl = "https://api.burrayhr.com";
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        employee_id_prefix: '',
        address: '',
        email: '',
        phone_number: '+263',
        city: '',
        md_email: '',
        logo: null,
        gratuity_start_years: '3',
        gratuity_increment_type: 'incremental'
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Special handling for phone field to maintain the country code
        if (name === 'phone_number') {
            if (value.startsWith('+263')) {
                setFormData({
                    ...formData,
                    [name]: value
                });
            } else {
                setFormData({
                    ...formData,
                    [name]: '+263' + value.replace(/^\+263/, '')
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = localStorage.getItem('token');
            
            // Create FormData object to handle file upload
            const submitData = new FormData();
            
            // Append all form fields to FormData
            Object.keys(formData).forEach(key => {
                if (key === 'logo' && formData[key]) {
                    submitData.append('logo', formData[key]);
                } else if (formData[key] !== null) {
                    submitData.append(key, formData[key]);
                }
            });

            // Make the API request with FormData
            await axios.post(`${baseUrl}/api/companies/`, submitData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Company created successfully',
                showConfirmButton: false,
                timer: 1500
            });

            navigate('/companies/view');
        } catch (error) {
            console.error('Error creating company:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to create company'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Topbar />
            <Sidebar />
            <div className="main-content">
                <div className="create-company-container">
                    <div className="create-company-header">
                        <FaBuilding className="header-icon" />
                        <h1>Add New Company</h1>
                    </div>

                    
                    
                    <form onSubmit={handleSubmit} className="create-company-form">
                        <div className="form-group">
                            <label htmlFor="logo">
                                <FaBuilding /> Company Logo
                            </label>
                            <input
                                type="file"
                                id="logo"
                                name="logo"
                                onChange={(e) => setFormData({ ...formData, logo: e.target.files[0] })}
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="name">
                                <FaBuilding /> Registered Company Name
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Enter registered company name"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="city">
                                <FaMapMarkerAlt /> City
                            </label>
                            <input
                                type="tel"
                                id="city"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                placeholder="Enter city"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="md_email">
                                <FaEnvelope /> Managing Director Email
                            </label>
                            <input
                                type="email"
                                id="md_email"
                                name="md_email"
                                value={formData.md_email}
                                onChange={handleChange}
                                placeholder="Enter Managing Director email"
                                required
                            />
                        </div>

                        
                        <div className="form-group">
                            <label htmlFor="name">
                                <FaBuilding /> Company ID Prefix
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="employee_id_prefix"
                                value={formData.employee_id_prefix}
                                onChange={handleChange}
                                placeholder="Enter company ID prefix"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="email">
                                <FaEnvelope /> Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="Enter company email"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="phone">
                                <FaPhone /> Phone Number
                            </label>
                            <input
                                type="tel"
                                id="phone_number"
                                name="phone_number"
                                value={formData.phone_number}
                                onChange={handleChange}
                                placeholder="Enter phone number"
                                required
                            />
                            <small className="input-help">Phone number must include country code +263</small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="address">
                                <FaMapMarkerAlt /> Address
                            </label>
                            <textarea
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="Enter company address"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="gratuity_increment_type">
                                <FaChartLine /> Gratuity Increment Type
                            </label>
                            <select
                                id="gratuity_increment_type"
                                name="gratuity_increment_type"
                                value={formData.gratuity_increment_type}
                                onChange={handleChange}
                                required
                            >
                                <option value="fixed">Fixed</option>
                                <option value="incremental">Incremental</option>
                            </select>
                        </div>

                        {formData.gratuity_increment_type === 'incremental' && (
                            <div className="form-group">
                                <label htmlFor="gratuity_start_years">
                                    <FaCalendarAlt /> Gratuity Starting Years
                                </label>
                                <select
                                    id="gratuity_start_years"
                                    name="gratuity_start_years"
                                    value={formData.gratuity_start_years}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="3">3 Years (10%)</option>
                                    <option value="4">4 Years (10%)</option>
                                    <option value="5">5 Years (10%)</option>
                                </select>
                            </div>
                        )}

                        <div className="form-actions">
                            <button 
                                type="button" 
                                className="cancel-btn"
                                onClick={() => navigate('/companies/view')}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="submit-btn" 
                                disabled={loading}
                            >
                                {loading ? 'Creating...' : 'Create Company'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateCompany;
